// Lib
import React from 'react';
import { useSelector } from 'react-redux';
import { Surface, UtilityButton } from '@milanote/roma';
import { EditorToolbar } from '@milanote/roma/application';

// Utils
import {
    getElementId,
    getFileMimeType,
    getImageProp,
} from '../../../../../../common/elements/utils/elementPropertyUtils';
import getFilenameFromUrl from '../../../../../../common/files/getFilenameFromUrl';
import { getLargestImageSize } from '../../imageModalUtils';
import { getImageSource } from '../../../imageHelper';
import { registerDownloadSource } from '../../../../../utils/fileDownloader/javascriptToSwiftFileDownloads';
import { isPlatformNativeApp } from '../../../../../platform/utils/platformDetailsUtils';

// Selectors
import { getIsFeatureEnabledForCurrentUser } from '../../../../feature/elementFeatureSelector';
import { getPlatformDetailsSelector } from '../../../../../platform/platformSelector';

// Actions
import { useTriggerDownload } from '../../../../attachments/attachmentDownloadActions';
import { trackAttachmentDownload } from '../../../../attachments/attachmentsService';

// Components
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    CropRotateIcon,
    Download02Icon,
    LinkExternal01Icon,
    Pencil02Icon,
    RemoveBg03Icon,
} from '@milanote/roma-icons';

// Hooks
import { useTranslator } from '../../../../../components/i18n/useTranslator';

// Constants
import { ExperimentId } from '../../../../../../common/experiments/experimentsConstants';

// Types
import { ImageModalV2Mode } from '../hooks/useImageModalV2Mode';
import { isOriginalUploadTransparent } from '../../backgroundRemoval/imageModalBackgroundRemovalUtils';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

type ImageModalV2PrimaryToolsProps = {
    element: ImMNElement;
    mode: ImageModalV2Mode;
    isEditable: boolean;
    showDownloadOption: boolean;
    isPresentationMode: boolean;
    canNavigate: boolean;
    navigateToPreviousImage: () => void;
    navigateToNextImage: () => void;
    setMode: (mode: ImageModalV2Mode) => void;
};

const PRIMARY_TOOL_BUTTON_CLASS_NAME = '!text-white hover:!text-white disabled:cursor-default disabled:opacity-45';

const ImageModalV2PrimaryTools: React.FC<ImageModalV2PrimaryToolsProps> = ({
    element,
    mode,
    isEditable,
    showDownloadOption,
    isPresentationMode,
    canNavigate,
    navigateToPreviousImage,
    navigateToNextImage,
    setMode,
}) => {
    const translate = useTranslator('popup', 'imageModal');
    const triggerDownload = useTriggerDownload();
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const isActive = (target: ImageModalV2Mode) => mode === target;

    const toggleMode = (target: ImageModalV2Mode) => setMode(isActive(target) ? ImageModalV2Mode.VIEW : target);

    const elementId = getElementId(element);
    const imageType = getFileMimeType(element) || 'image/jpeg';
    // Background removal is released separately from the modal itself.
    const isBackgroundRemovalEnabled = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.imageBackgroundRemoval),
    );
    const hideBackgroundRemoval = !isBackgroundRemovalEnabled || isOriginalUploadTransparent(element);
    const imageDetails = getImageProp(element);
    const renderSecureImagesThroughCF = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughCF, true),
    );

    const largestImageUrl = getImageSource({
        imageDetails,
        imageSize: getLargestImageSize(imageDetails),
        elementId,
        useSecureMediaUrl: true,
        optimizeForWebRender: false,
        renderSecureImagesThroughCF,
    });

    const filename = largestImageUrl ? getFilenameFromUrl(largestImageUrl, imageType) : '';
    const showOpenLinkInBrowser = largestImageUrl && showDownloadOption && !isPlatformNativeApp(platformDetails);
    const showDownloadButton = largestImageUrl && showDownloadOption && !isPresentationMode;

    const openLargestImageInBrowser = () => {
        if (!largestImageUrl) return;

        trackAttachmentDownload({ url: largestImageUrl });

        const newWindow = window.open(largestImageUrl, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
    };

    const downloadLargestImage = () => {
        if (!largestImageUrl) return;

        trackAttachmentDownload({ url: largestImageUrl });

        triggerDownload({
            url: largestImageUrl,
            filename,
            onDownloadStart: () => registerDownloadSource('.ImageModalV2PrimaryTools-downloadButton'),
            id: elementId,
            isShare: false,
        });
    };

    return (
        <EditorToolbar.Shell className="ImageModalV2PrimaryTools pointer-events-auto bg-transparent shadow-none">
            <Surface surface="dark">
                <EditorToolbar.List>
                    <UtilityButton
                        className={PRIMARY_TOOL_BUTTON_CLASS_NAME}
                        isDisabled={!canNavigate}
                        onPress={navigateToPreviousImage}
                        tooltip={{ title: translate('back') }}
                    >
                        <ArrowLeftIcon />
                    </UtilityButton>
                    <UtilityButton
                        className={PRIMARY_TOOL_BUTTON_CLASS_NAME}
                        isDisabled={!canNavigate}
                        onPress={navigateToNextImage}
                        tooltip={{ title: translate('next') }}
                    >
                        <ArrowRightIcon />
                    </UtilityButton>

                    <EditorToolbar.Divider />

                    {isEditable && (
                        <UtilityButton
                            className={PRIMARY_TOOL_BUTTON_CLASS_NAME}
                            isSelected={isActive(ImageModalV2Mode.DRAW)}
                            onPress={() => toggleMode(ImageModalV2Mode.DRAW)}
                            tooltip={{ title: translate('drawOn') }}
                        >
                            <Pencil02Icon />
                        </UtilityButton>
                    )}

                    {isEditable && !isPresentationMode && (
                        <UtilityButton
                            className={PRIMARY_TOOL_BUTTON_CLASS_NAME}
                            isSelected={isActive(ImageModalV2Mode.CROP)}
                            onPress={() => toggleMode(ImageModalV2Mode.CROP)}
                            tooltip={{ title: translate('edit') }}
                        >
                            <CropRotateIcon />
                        </UtilityButton>
                    )}

                    {isEditable && !isPresentationMode && largestImageUrl && !hideBackgroundRemoval && (
                        <UtilityButton
                            className={PRIMARY_TOOL_BUTTON_CLASS_NAME}
                            isSelected={isActive(ImageModalV2Mode.BACKGROUND)}
                            onPress={() => toggleMode(ImageModalV2Mode.BACKGROUND)}
                            tooltip={{ title: translate('removeBackground') }}
                        >
                            <RemoveBg03Icon />
                        </UtilityButton>
                    )}

                    {showOpenLinkInBrowser && (
                        <UtilityButton
                            className={PRIMARY_TOOL_BUTTON_CLASS_NAME}
                            onPress={openLargestImageInBrowser}
                            tooltip={{ title: translate('openInNewTab') }}
                        >
                            <LinkExternal01Icon />
                        </UtilityButton>
                    )}

                    {showDownloadButton && (
                        <UtilityButton
                            className={`ImageModalV2PrimaryTools-downloadButton ${PRIMARY_TOOL_BUTTON_CLASS_NAME}`}
                            onPress={downloadLargestImage}
                            tooltip={{ title: translate('download') }}
                        >
                            <Download02Icon />
                        </UtilityButton>
                    )}
                </EditorToolbar.List>
            </Surface>
        </EditorToolbar.Shell>
    );
};

export default ImageModalV2PrimaryTools;
